/**
 * กะทำงานของฉัน — พนักงานดูตารางกะตัวเอง (โจทย์ลูกค้า: เลือกดูได้ รายวัน / รายสัปดาห์ / รายเดือน)
 * ข้อมูลจาก GET /me/shifts (roster > assignment + วันหยุดบริษัท + วันหยุดประจำสัปดาห์)
 */
import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, CalendarClock, Moon } from 'lucide-react';
import { PageLayout } from '../components/layout/PageLayout';
import { useMyShifts, type MyShiftDay } from '../hooks';

type ViewMode = 'day' | 'week' | 'month';
const VIEW_LABEL: Record<ViewMode, string> = { day: 'รายวัน', week: 'รายสัปดาห์', month: 'รายเดือน' };

const THAI_MONTHS_SHORT = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
const THAI_DOW = ['อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์'];
const DOW_HEADERS = ['จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส', 'อา'];

const pad = (n: number) => String(n).padStart(2, '0');
const iso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const todayIso = () => iso(new Date());
const parseIso = (s: string) => new Date(`${s}T00:00:00`);
const addDays = (s: string, n: number) => { const d = parseIso(s); d.setDate(d.getDate() + n); return iso(d); };
/** วันจันทร์ของสัปดาห์ที่มีวันนี้อยู่ */
const mondayOf = (s: string) => { const d = parseIso(s); const shift = (d.getDay() + 6) % 7; return addDays(s, -shift); };
/** วันเริ่มรอบ 26–25 ที่ครอบวันนี้ (>=26 → 26 เดือนนี้, <26 → 26 เดือนก่อน) */
const cycleStartOf = (s: string) => {
  const d = parseIso(s);
  if (d.getDate() >= 26) return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-26`;
  return iso(new Date(d.getFullYear(), d.getMonth() - 1, 26));
};
/** วันสิ้นรอบ = 25 ของเดือนถัดจากวันเริ่มรอบ */
const cycleEndOf = (startIso: string) => {
  const d = parseIso(startIso);
  return iso(new Date(d.getFullYear(), d.getMonth() + 1, 25));
};

function thaiDate(s: string, withDow = true): string {
  const d = parseIso(s);
  const base = `${d.getDate()} ${THAI_MONTHS_SHORT[d.getMonth()]} ${d.getFullYear() + 543}`;
  return withDow ? `${THAI_DOW[d.getDay()]} ${base}` : base;
}

/** สรุปสถานะของวันหนึ่งวันเป็นข้อความ + โทนสี */
function dayStatus(day: MyShiftDay): { label: string; detail: string | null; tone: 'work' | 'off' | 'holiday' | 'standard' } {
  if (day.holiday) return { label: 'วันหยุดบริษัท', detail: day.holiday, tone: 'holiday' };
  if (day.shift) {
    return {
      label: day.shift.name,
      detail: `${day.shift.start_time}–${day.shift.end_time}${day.shift.flexible ? ' · ยืดหยุ่น' : ''}`,
      tone: 'work',
    };
  }
  if (day.day_off) return { label: 'วันหยุด', detail: null, tone: 'off' };
  return { label: 'เวลาทำงานมาตรฐาน', detail: null, tone: 'standard' };
}

const TONE_CHIP: Record<string, string> = {
  work: 'bg-brand-50 text-brand-700',
  off: 'bg-gray-100 text-gray-500',
  holiday: 'bg-red-50 text-red-600',
  standard: 'bg-teal-50 text-teal-700',
};

export function MySchedule() {
  const [view, setView] = useState<ViewMode>('week');
  const [anchor, setAnchor] = useState(todayIso());

  // ช่วงวันที่ตามมุมมอง
  const { from, to } = useMemo(() => {
    if (view === 'day') return { from: anchor, to: anchor };
    if (view === 'week') { const mon = mondayOf(anchor); return { from: mon, to: addDays(mon, 6) }; }
    // รายเดือน = รอบ 26–25 (ไม่ใช่ 1–สิ้นเดือน)
    const start = cycleStartOf(anchor);
    return { from: start, to: cycleEndOf(start) };
  }, [view, anchor]);

  const { data, isLoading } = useMyShifts(from, to);
  const days = data?.days ?? [];
  const byDate = new Map(days.map((d) => [d.date, d]));
  const today = todayIso();

  function navigate(dir: -1 | 1) {
    if (view === 'day') setAnchor(addDays(anchor, dir));
    else if (view === 'week') setAnchor(addDays(anchor, dir * 7));
    else {
      // เลื่อนทีละรอบ 26–25 (วันเริ่มรอบเป็นวันที่ 26 เสมอ → บวก/ลบเดือนได้ตรง)
      const d = parseIso(cycleStartOf(anchor));
      d.setMonth(d.getMonth() + dir);
      setAnchor(iso(d));
    }
  }

  const headerLabel = useMemo(() => {
    if (view === 'day') return thaiDate(anchor);
    if (view === 'week') return `${thaiDate(from, false)} – ${thaiDate(to, false)}`;
    const start = parseIso(cycleStartOf(anchor));
    const end = parseIso(cycleEndOf(cycleStartOf(anchor)));
    return `รอบ 26 ${THAI_MONTHS_SHORT[start.getMonth()]} – 25 ${THAI_MONTHS_SHORT[end.getMonth()]} ${end.getFullYear() + 543}`;
  }, [view, anchor, from, to]);

  return (
    <PageLayout title="กะทำงานของฉัน">
      <div className="space-y-3 pb-10">
        {/* สลับมุมมอง */}
        <div className="flex rounded-xl bg-gray-100 p-1">
          {(['day', 'week', 'month'] as const).map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`flex-1 py-1.5 text-sm font-medium rounded-lg transition ${
                view === v ? 'bg-white text-brand-700 shadow-sm' : 'text-gray-500'
              }`}
            >
              {VIEW_LABEL[v]}
            </button>
          ))}
        </div>

        {/* แถบเลื่อนช่วงเวลา */}
        <div className="flex items-center justify-between gap-2">
          <button onClick={() => navigate(-1)} className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="text-sm font-medium text-gray-800 text-center flex-1">{headerLabel}</div>
          <button onClick={() => navigate(1)} className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        {anchor !== today && (
          <button onClick={() => setAnchor(today)} className="text-xs text-brand-600 hover:underline">
            กลับมาวันนี้
          </button>
        )}

        {isLoading ? (
          <p className="text-center text-gray-400 py-10 text-sm">กำลังโหลด…</p>
        ) : view === 'day' ? (
          <DayCard day={byDate.get(anchor)} isToday={anchor === today} />
        ) : view === 'week' ? (
          <div className="card divide-y divide-gray-100">
            {days.map((d) => {
              const st = dayStatus(d);
              const isToday = d.date === today;
              return (
                <div key={d.date} className={`flex items-center gap-3 px-3 py-2.5 ${isToday ? 'bg-brand-50/50' : ''}`}>
                  <div className="w-20 shrink-0">
                    <div className={`text-sm font-medium ${isToday ? 'text-brand-700' : 'text-gray-800'}`}>
                      {THAI_DOW[parseIso(d.date).getDay()]}
                    </div>
                    <div className="text-[11px] text-gray-400">
                      {parseIso(d.date).getDate()} {THAI_MONTHS_SHORT[parseIso(d.date).getMonth()]}{isToday ? ' · วันนี้' : ''}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className={`inline-block rounded-lg px-2 py-0.5 text-xs font-medium ${TONE_CHIP[st.tone]}`}>{st.label}</span>
                    {st.detail && <div className="text-[11px] text-gray-500 mt-0.5 truncate">{st.detail}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <MonthGrid days={days} anchor={anchor} today={today} onPickDay={(d) => { setAnchor(d); setView('day'); }} />
        )}

        {data?.attendance_exempt && (
          <p className="text-[11px] text-gray-400">คุณอยู่ในกลุ่มไม่ต้องสแกนเวลา — ตารางนี้แสดงเพื่ออ้างอิงเท่านั้น</p>
        )}
      </div>
    </PageLayout>
  );
}

function DayCard({ day, isToday }: { day: MyShiftDay | undefined; isToday: boolean }) {
  if (!day) return <p className="text-center text-gray-400 py-10 text-sm">ไม่มีข้อมูล</p>;
  const st = dayStatus(day);
  return (
    <div className="card p-5 text-center space-y-2">
      {isToday && <div className="text-xs font-medium text-brand-600">วันนี้</div>}
      <div className="flex items-center justify-center gap-2">
        {st.tone === 'work' && day.shift?.flexible === false && day.shift.start_time >= '18:00' ? (
          <Moon className="w-5 h-5 text-purple-500" />
        ) : (
          <CalendarClock className="w-5 h-5 text-brand-500" />
        )}
        <span className={`rounded-lg px-3 py-1 text-base font-semibold ${TONE_CHIP[st.tone]}`}>{st.label}</span>
      </div>
      {st.detail && <div className="text-sm text-gray-600">{st.detail}</div>}
      {st.tone === 'standard' && (
        <p className="text-[11px] text-gray-400">วันนี้ไม่ได้กำหนดกะเฉพาะ — ใช้เวลาทำงานมาตรฐานของบริษัท</p>
      )}
    </div>
  );
}

function MonthGrid({ days, anchor, today, onPickDay }: {
  days: MyShiftDay[];
  anchor: string;
  today: string;
  onPickDay: (date: string) => void;
}) {
  const start = parseIso(cycleStartOf(anchor));
  const end = parseIso(cycleEndOf(cycleStartOf(anchor)));
  const byDate = new Map(days.map((x) => [x.date, x]));
  // ไล่วันจากวันเริ่มรอบ (26) ถึงวันสิ้นรอบ (25) — ข้ามเดือน
  const dateList: string[] = [];
  for (const cur = new Date(start); cur <= end; cur.setDate(cur.getDate() + 1)) dateList.push(iso(cur));
  // grid เริ่มวันจันทร์ (ให้ตรงกับหัวตาราง จ–อา)
  const firstDow = (start.getDay() + 6) % 7;
  const cells: Array<string | null> = [
    ...Array<null>(firstDow).fill(null),
    ...dateList,
  ];

  return (
    <div className="space-y-1">
      <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-medium text-gray-400">
        {DOW_HEADERS.map((h, i) => <div key={h} className={i >= 5 ? 'text-red-400' : ''}>{h}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((dateIso, idx) => {
          if (!dateIso) return <div key={`b${idx}`} />;
          const day = byDate.get(dateIso);
          const st = day ? dayStatus(day) : null;
          const isToday = dateIso === today;
          return (
            <button
              key={dateIso}
              onClick={() => onPickDay(dateIso)}
              className={`min-h-[58px] rounded-lg border p-1 text-left flex flex-col gap-0.5 ${
                isToday ? 'border-brand-400 bg-brand-50/40' : 'border-gray-100 bg-white'
              }`}
            >
              <div className={`text-[11px] font-medium ${isToday ? 'text-brand-700' : 'text-gray-600'}`}>
                {parseIso(dateIso).getDate()}
              </div>
              {st && (
                <div className={`text-[9px] leading-tight rounded px-1 py-0.5 truncate ${TONE_CHIP[st.tone]}`}>
                  {st.tone === 'work' && st.detail ? st.detail.split(' ')[0] : st.label}
                </div>
              )}
            </button>
          );
        })}
      </div>
      <div className="flex items-center gap-3 text-[11px] text-gray-500 flex-wrap pt-1">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-brand-50 border border-brand-200" /> มีกะ</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-gray-100 border border-gray-200" /> วันหยุด</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-50 border border-red-200" /> วันหยุดบริษัท</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-teal-50 border border-teal-200" /> เวลามาตรฐาน</span>
      </div>
    </div>
  );
}
